import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FloatField } from "@/components/ui/FormField";
import { XCircle, Loader2 } from "lucide-react";
import type { HRDocument } from "@/lib/api";
import { apiRejectHRDocument } from "@/lib/api";
import { toast } from "sonner";

interface Props {
  doc: HRDocument;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDocumentUpdated: (doc: HRDocument) => void;
}

export function RejectDocumentModal({
  doc,
  open,
  onOpenChange,
  onDocumentUpdated,
}: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      toast.error(t("documents.messages.rejectionReasonRequired"));
      return;
    }

    setLoading(true);
    try {
      const updated = await apiRejectHRDocument(doc.id, reason.trim());
      onDocumentUpdated(updated);
      toast.success(t("documents.messages.documentRejected"), {
        description: t("documents.messages.documentRejectedDesc", {
          name: doc.documentName,
        }),
      });
      setReason("");
      onOpenChange(false);
    } catch (err: any) {
      toast.error(t("documents.messages.rejectFailed"), { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (val: boolean) => {
    if (!loading) {
      if (!val) setReason("");
      onOpenChange(val);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 bg-red-50 dark:bg-red-900/10 border-b border-red-100 dark:border-red-900/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold text-red-700 dark:text-red-400">
              <div className="h-8 w-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              </div>
              {t("documents.rejectModal.title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mt-2">
            {t("documents.rejectModal.description", {
              name: doc.documentName,
            })}
          </p>
        </div>

        <div className="px-6 py-5">
          <FloatField
            label={t("documents.rejectModal.reasonLabel")}
            type="textarea"
            required
            placeholder={t("documents.rejectModal.reasonPlaceholder")}
            value={reason}
            onChange={(v) => setReason(v as string)}
          />
          {reason.trim().length === 0 && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {t("documents.rejectModal.reasonHint")}
            </p>
          )}
        </div>

        <div className="px-6 pb-6 pt-1 border-t bg-muted/20 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={loading || !reason.trim()}
          >
            {loading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {t("documents.rejectModal.rejecting")}
              </>
            ) : (
              <>
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                {t("documents.rejectDocument")}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
